import { useEffect, useState } from "react";
import { Calendar, Check, Clock, ExternalLink, MapPin, Phone } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import NotFound from "@/pages/NotFound";
import { HELOC_EVENT, HELOC_EVENT_SLUG, type PublicEvent } from "@/data/events";
import { supabase } from "@/integrations/supabase/client";
import { isEventPast } from "@/lib/eventDates";

const EventDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const fallback = slug === HELOC_EVENT_SLUG ? HELOC_EVENT : null;
  const [event, setEvent] = useState<PublicEvent | null>(fallback);
  const [loading, setLoading] = useState(!fallback);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!event || !slug) return;
    document.title = event.seo_title;
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute("content", event.seo_description);
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.href = `https://hadassahhomesinc.com${event.canonical_path}`;
    }
  }, [event, slug]);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      setNotFound(true);
      return;
    }

    if (fallback) {
      setEvent(fallback);
      setLoading(false);
      setNotFound(false);
      return;
    }

    let mounted = true;
    void (async () => {
      const { data, error } = await supabase
        .from("events")
        .select(
          "title,slug,event_date,date_time_label,time_range,location_name,location_line_1,location_line_2,format,category,description,registration_url,image_url"
        )
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (!mounted) return;
      if (error) {
        setLoading(false);
        setNotFound(true);
        return;
      }
      if (!data) {
        setEvent(null);
        setNotFound(true);
        setLoading(false);
        return;
      }

      setEvent({
        ...data,
        presenter_name: null,
        partner_names: [],
        flyer_en_url: null,
        flyer_es_url: null,
        topics: [],
        event_notes: [],
        contact_phone: null,
        announcement_text: null,
        flyer_en_alt: null,
        flyer_es_alt: null,
        start_date_time: "",
        end_date_time: "",
        seo_title: `${data.title} | Hadassah Homes`,
        seo_description: data.description || `View event details for ${data.title}.`,
        canonical_path: `/events/${data.slug}`,
      });
      setNotFound(false);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [fallback, slug]);

  if (notFound) return <NotFound />;

  if (loading || !event) {
    return (
      <SiteLayout>
        <div className="flex min-h-[70vh] items-center justify-center bg-[#F8F5EE] px-5 py-28">
          <p className="font-body text-sm uppercase tracking-[0.16em] text-[#6B655A]">
            Loading event details…
          </p>
        </div>
      </SiteLayout>
    );
  }

  const telHref = event.contact_phone
    ? `tel:${event.contact_phone.replace(/[^+\d]/g, "")}`
    : null;
  const flyers = [
    {
      label: "English",
      url: event.flyer_en_url,
      alt: event.flyer_en_alt || `English flyer for ${event.title}.`,
    },
    {
      label: "Español",
      url: event.flyer_es_url,
      alt: event.flyer_es_alt || `Spanish flyer for ${event.title}.`,
    },
  ].filter((flyer): flyer is { label: string; url: string; alt: string } => Boolean(flyer.url));
  const past = isEventPast(event.event_date);
  const partners = event.partner_names ?? [];
  const attribution = event.presenter_name
    ? `Presented by ${event.presenter_name}${
        partners.length > 0
          ? `, in partnership with ${partners.join(" and ")}`
          : ""
      }.`
    : partners.length > 0
      ? `In partnership with ${partners.join(" and ")}.`
      : null;

  return (
    <SiteLayout>
      <article>
          <header className="relative isolate overflow-hidden bg-[#102C21] px-5 pb-20 pt-32 text-white md:px-10 md:pb-28 md:pt-40">
            {event.image_url ? (
              <img
                src={event.image_url}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 -z-20 h-full w-full object-cover"
              />
            ) : null}
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/90 via-black/72 to-black/45" />
            <div className="mx-auto max-w-6xl">
              <nav aria-label="Breadcrumb" className="mb-10 flex flex-wrap items-center gap-2 text-sm text-white/70">
                <Link to="/" className="transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A95C]">
                  Home
                </Link>
                <span aria-hidden="true">/</span>
                <Link to="/events" className="transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A95C]">
                  Events
                </Link>
                <span aria-hidden="true">/</span>
                <span aria-current="page" className="text-white/90">{event.title}</span>
              </nav>

              <div className="max-w-4xl space-y-7">
                <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-[#D9BC75]">
                  {event.category || "Event"}
                </p>
                <h1 className="font-headline text-4xl leading-[1.08] sm:text-5xl md:text-6xl lg:text-7xl">
                  {event.title}
                </h1>
                <div className="grid max-w-3xl gap-5 border-y border-white/20 py-6 text-white/90 sm:grid-cols-2">
                  <div className="flex items-start gap-3">
                    <Calendar className="mt-1 h-5 w-5 shrink-0 text-[#D9BC75]" aria-hidden="true" />
                    <div>
                      <p>{event.date_time_label}</p>
                      <p className="mt-1 text-white/70">{event.time_range}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#D9BC75]" aria-hidden="true" />
                    <div>
                      <p>{event.location_name}</p>
                      <p className="mt-1 text-white/70">{event.location_line_1}</p>
                      <p className="text-white/70">{event.location_line_2}</p>
                    </div>
                  </div>
                </div>
                <p className="font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#D9BC75]">
                  Free to attend
                </p>
                {!past && event.registration_url ? (
                  <a
                    href={event.registration_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-12 w-full items-center justify-center gap-2 bg-[#C9A95C] px-7 py-4 font-body text-sm font-semibold uppercase tracking-[0.12em] text-[#102C21] transition-colors hover:bg-[#DFC77F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#102C21] sm:w-auto"
                  >
                    Register for Free
                    <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  </a>
                ) : past ? (
                  <span className="inline-flex min-h-12 w-full items-center justify-center border border-white/35 px-7 py-4 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white/85 sm:w-auto">
                    This Event Has Passed
                  </span>
                ) : null}
              </div>
            </div>
          </header>

          <section className="bg-[#F8F5EE] px-5 py-16 md:px-10 md:py-24">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
              <div className="space-y-6">
                <p className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#A8873F]">Community Education</p>
                <p className="font-headline text-2xl leading-relaxed text-[#173D2D] md:text-3xl">
                  {event.description}
                </p>
                {attribution ? (
                  <p className="text-base leading-relaxed text-[#5C574E]">{attribution}</p>
                ) : null}
              </div>

              {(event.topics ?? []).length > 0 ? (
                <div className="border border-[#C9A95C]/30 bg-white p-7 shadow-[0_20px_60px_rgba(23,61,45,0.08)] md:p-9">
                  <h2 className="font-headline text-3xl text-[#173D2D]">What You&apos;ll Learn</h2>
                  <ul className="mt-7 space-y-4">
                    {(event.topics ?? []).map((topic) => (
                      <li key={topic} className="flex items-start gap-3 text-[#4F4A42]">
                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#A8873F]" aria-hidden="true" />
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </section>

          <section className="bg-white px-5 py-16 md:px-10 md:py-24">
            <div className="mx-auto max-w-6xl">
              <div className="mb-10 max-w-2xl">
                <p className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#A8873F]">Plan Your Visit</p>
                <h2 className="mt-4 font-headline text-4xl text-[#173D2D] md:text-5xl">Event Details</h2>
              </div>
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                <Detail icon={Calendar} label="Date" value={event.date_time_label} />
                <Detail icon={Clock} label="Time" value={event.time_range} />
                <Detail
                  icon={MapPin}
                  label="Location"
                  value={[event.location_name, event.location_line_1, event.location_line_2].filter(Boolean).join("\n")}
                />
                <Detail label="Admission" value="Free" />
                {(event.event_notes ?? []).map((note) => (
                  <Detail key={note} label="Good to Know" value={note} />
                ))}
                {event.contact_phone ? (
                  <div className="border-t border-[#C9A95C]/30 py-5">
                    <div className="flex items-center gap-2 text-[#A8873F]">
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      <p className="font-body text-xs font-semibold uppercase tracking-[0.16em]">More Information</p>
                    </div>
                    <a href={telHref ?? undefined} className="mt-3 inline-block text-lg text-[#173D2D] underline decoration-[#C9A95C] underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#173D2D]">
                      {event.contact_phone}
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          {flyers.length > 0 ? (
            <section className="bg-[#102C21] px-5 py-16 text-white md:px-10 md:py-24">
              <div className="mx-auto max-w-6xl">
                <div className="mb-10 max-w-2xl">
                  <p className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#D9BC75]">English &amp; Spanish</p>
                  <h2 className="mt-4 font-headline text-4xl md:text-5xl">Event Flyers</h2>
                </div>
                <div className="grid gap-8 md:grid-cols-2">
                  {flyers.map((flyer) => (
                    <figure key={flyer.label} className="min-w-0 border border-white/15 bg-white/5 p-4 sm:p-6">
                      <figcaption className="mb-4 font-body text-sm font-semibold uppercase tracking-[0.16em] text-[#D9BC75]">
                        {flyer.label}
                      </figcaption>
                      <img
                        src={flyer.url}
                        alt={flyer.alt}
                        width={1080}
                        height={1350}
                        loading="lazy"
                        className="h-auto w-full bg-white object-contain"
                      />
                    </figure>
                  ))}
                </div>
              </div>
            </section>
          ) : null}
      </article>
    </SiteLayout>
  );
};

const Detail = ({
  icon: Icon,
  label,
  value,
}: {
  icon?: typeof Calendar;
  label: string;
  value: string | null;
}) => {
  if (!value) return null;
  return (
    <div className="border-t border-[#C9A95C]/30 py-5">
      <div className="flex items-center gap-2 text-[#A8873F]">
        {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
        <p className="font-body text-xs font-semibold uppercase tracking-[0.16em]">{label}</p>
      </div>
      <p className="mt-3 whitespace-pre-line text-lg leading-relaxed text-[#173D2D]">{value}</p>
    </div>
  );
};

export default EventDetail;
